import { Copy } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { copyText } from '@/lib/clipboard';

/** Generalised in sprint 5 (§6.9): invites and share links pass their own texts. */
export interface RevealedLink {
  link: string;
  title: string;
  description: ReactNode;
  inputLabel: string;
}

interface LinkRevealDialogProps {
  /** null = closed. The parent drops the link from state on close, so it is never shown twice. */
  value: RevealedLink | null;
  onClose: () => void;
}

export function LinkRevealDialog({ value, onClose }: LinkRevealDialogProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'manual'>('idle');

  async function onCopy(): Promise<void> {
    if (!value) return;
    if (await copyText(value.link)) {
      setCopyState('copied');
      return;
    }
    inputRef.current?.select();
    setCopyState('manual');
  }

  return (
    <Dialog
      open={value !== null}
      onOpenChange={(open) => {
        if (!open) {
          setCopyState('idle');
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{value?.title ?? ''}</DialogTitle>
          <DialogDescription>{value?.description ?? ''}</DialogDescription>
        </DialogHeader>
        <Input
          ref={inputRef}
          readOnly
          value={value?.link ?? ''}
          aria-label={value?.inputLabel ?? 'Link'}
          onFocus={(e) => e.currentTarget.select()}
          className="font-mono text-xs"
        />
        {copyState === 'copied' && <p className="text-sm text-success-fg">Link copied.</p>}
        {copyState === 'manual' && <p className="text-sm text-warning-fg">Press Ctrl+C to copy.</p>}
        <DialogFooter>
          <Button className="flex-1" onClick={() => void onCopy()}>
            <Copy aria-hidden="true" />
            Copy link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
